import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, MicOff, Play, Square, FileText, Pill, Download, 
  CheckCircle, Edit2, Save, X, User, Stethoscope, Clock,
  AlertCircle, RefreshCw, Upload, ChevronDown, ChevronUp,
  Plus, Trash2, Send, Volume2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PatientSearchDropdown } from '@/components/SearchDropdowns';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AIScribe() {
  const user = JSON.parse(localStorage.getItem('user'));
  
  // Session state
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState('doctor');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Language options
  const LANGUAGES = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'hi', name: 'हिंदी (Hindi)', flag: '🇮🇳' },
    { code: 'ta', name: 'தமிழ் (Tamil)', flag: '🇮🇳' },
    { code: 'te', name: 'తెలుగు (Telugu)', flag: '🇮🇳' },
    { code: 'kn', name: 'ಕನ್ನಡ (Kannada)', flag: '🇮🇳' },
    { code: 'mr', name: 'मराठी (Marathi)', flag: '🇮🇳' }
  ];
  
  // UI state
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [patients, setPatients] = useState([]);
  const [visits, setVisits] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedVisit, setSelectedVisit] = useState('');
  
  // Processing state
  const [transcribing, setTranscribing] = useState(false);
  const [generatingSoap, setGeneratingSoap] = useState(false);
  const [generatingRx, setGeneratingRx] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Edit state
  const [editingSoap, setEditingSoap] = useState(false);
  const [editingRx, setEditingRx] = useState(false);
  const [editedSoap, setEditedSoap] = useState(null);
  const [editedRx, setEditedRx] = useState(null);
  
  // Expanded sections
  const [transcriptExpanded, setTranscriptExpanded] = useState(true);
  const [soapExpanded, setSoapExpanded] = useState(true);
  const [rxExpanded, setRxExpanded] = useState(true);

  useEffect(() => {
    fetchSessions();
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchPatientVisits(selectedPatient);
    }
  }, [selectedPatient]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/ai-scribe/sessions', {
        params: { doctor_id: user.id }
      });
      setSessions(response.data);
    } catch (error) {
      toast.error('Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients');
    }
  };

  const fetchPatientVisits = async (patientId) => {
    try {
      const response = await api.get('/visits', { params: { patient_id: patientId, status: 'in_progress' } });
      setVisits(response.data);
    } catch (error) {
      console.error('Failed to fetch visits');
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await api.get(`/ai-scribe/sessions/${sessionId}`);
      setCurrentSession(response.data);
      setEditedSoap(response.data.soap_note);
      setEditedRx(response.data.prescription_draft);
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const createSession = async () => {
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    const patient = patients.find(p => p.id === selectedPatient);
    
    try {
      const response = await api.post('/ai-scribe/sessions', {
        patient_id: selectedPatient,
        visit_id: selectedVisit && selectedVisit !== 'none' ? selectedVisit : null,
        doctor_id: user.id,
        doctor_name: user.full_name,
        patient_name: patient?.full_name || 'Unknown'
      });
      
      setCurrentSession(response.data);
      setSessions([response.data, ...sessions]);
      setShowNewSessionDialog(false);
      setSelectedPatient('');
      setSelectedVisit('');
      toast.success('Session created');
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  // Audio Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      const langName = LANGUAGES.find(l => l.code === selectedLanguage)?.name || 'English';
      toast.success(`Recording as ${currentSpeaker === 'doctor' ? 'Doctor' : 'Patient'} in ${langName}...`);
    } catch (error) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob || !currentSession) return;

    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      formData.append('session_id', currentSession.id);
      formData.append('speaker', currentSpeaker);
      formData.append('language', selectedLanguage);

      const response = await api.post('/ai-scribe/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update current session with new transcript
      setCurrentSession(prev => ({
        ...prev,
        transcript: [...(prev.transcript || []), response.data.transcript]
      }));

      setAudioBlob(null);
      const wasTranslated = response.data.was_translated;
      toast.success(wasTranslated 
        ? `Transcribed & translated from ${response.data.language}` 
        : 'Transcription complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transcription failed');
    } finally {
      setTranscribing(false);
    }
  };

  const generateSoapNote = async () => {
    if (!currentSession) return;

    setGeneratingSoap(true);
    try {
      const response = await api.post(`/ai-scribe/sessions/${currentSession.id}/generate-soap`);
      setCurrentSession(prev => ({ ...prev, soap_note: response.data.soap_note }));
      setEditedSoap(response.data.soap_note);
      toast.success('SOAP note generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SOAP generation failed');
    } finally {
      setGeneratingSoap(false);
    }
  };

  const generatePrescription = async () => {
    if (!currentSession) return;

    setGeneratingRx(true);
    try {
      const response = await api.post(`/ai-scribe/sessions/${currentSession.id}/generate-prescription`);
      setCurrentSession(prev => ({ ...prev, prescription_draft: response.data.prescription_draft }));
      setEditedRx(response.data.prescription_draft);
      toast.success('Prescription draft generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Prescription generation failed');
    } finally {
      setGeneratingRx(false);
    }
  };

  const saveSoapEdit = async () => {
    try {
      await api.put(`/ai-scribe/sessions/${currentSession.id}/soap`, editedSoap);
      setCurrentSession(prev => ({ ...prev, soap_note: editedSoap }));
      setEditingSoap(false);
      toast.success('SOAP note updated');
    } catch (error) {
      toast.error('Failed to save SOAP note');
    }
  };

  const saveRxEdit = async () => {
    try {
      await api.put(`/ai-scribe/sessions/${currentSession.id}/prescription`, editedRx);
      setCurrentSession(prev => ({ ...prev, prescription_draft: editedRx }));
      setEditingRx(false);
      toast.success('Prescription updated');
    } catch (error) {
      toast.error('Failed to save prescription');
    }
  };

  const syncToPMS = async () => {
    if (!currentSession) return;

    setSyncing(true);
    try {
      const response = await api.post(`/ai-scribe/sessions/${currentSession.id}/sync-to-pms`, {
        session_id: currentSession.id,
        sync_to_visit: true,
        sync_to_medical_records: true,
        create_prescription: true  // Always create prescription when syncing
      });
      
      setCurrentSession(prev => ({ ...prev, status: 'synced' }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const exportSession = async () => {
    if (!currentSession) return;

    try {
      const response = await api.get(`/ai-scribe/sessions/${currentSession.id}/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scribe-session-${currentSession.id.slice(0, 8)}.json`;
      a.click();
      toast.success('Session exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const addManualTranscript = async (text, speaker) => {
    if (!text.trim() || !currentSession) return;

    try {
      const response = await api.post(`/ai-scribe/sessions/${currentSession.id}/add-transcript`, {
        speaker,
        text: text.trim(),
        timestamp: new Date().toISOString()
      });

      setCurrentSession(prev => ({
        ...prev,
        transcript: [...(prev.transcript || []), response.data.transcript]
      }));
    } catch (error) {
      toast.error('Failed to add transcript');
    }
  };

  // Render session list sidebar
  const renderSessionList = () => (
    <Card className="h-full border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Sessions</CardTitle>
          <Button 
            size="sm" 
            onClick={() => setShowNewSessionDialog(true)}
            className="bg-[#4A238F] hover:bg-[#3A1B72]"
            data-testid="new-session-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
        {sessions.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No sessions yet</p>
        ) : (
          sessions.map(session => (
            <button
              key={session.id}
              onClick={() => loadSession(session.id)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                currentSession?.id === session.id 
                  ? 'border-[#4A238F] bg-indigo-50' 
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm truncate">{session.patient_name}</span>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    session.status === 'synced' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    session.status === 'completed' ? 'bg-purple-100 text-blue-700 border-blue-200' :
                    'bg-amber-50 text-amber-700 border-amber-200'
                  }`}
                >
                  {session.status}
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                {new Date(session.created_at).toLocaleDateString()}
              </p>
            </button>
          ))
        )}
      </CardContent>
    </Card>
  );

  // Render main workspace
  const renderWorkspace = () => {
    if (!currentSession) {
      return (
        <Card className="h-full flex items-center justify-center border-slate-200">
          <div className="text-center py-12">
            <Stethoscope className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">AI Medical Scribe</h3>
            <p className="text-sm text-slate-500 mb-4">Select or create a session to begin</p>
            <Button 
              onClick={() => setShowNewSessionDialog(true)}
              className="bg-[#4A238F] hover:bg-[#3A1B72]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Start New Session
            </Button>
          </div>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {/* Session Header */}
        <Card className="border-slate-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-[#4A238F]" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">{currentSession.patient_name}</h2>
                  <p className="text-sm text-slate-500">
                    Dr. {currentSession.doctor_name} • {new Date(currentSession.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${
                  currentSession.status === 'synced' ? 'bg-emerald-100 text-emerald-700' :
                  currentSession.status === 'completed' ? 'bg-purple-200 text-blue-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {currentSession.status.toUpperCase()}
                </Badge>
                <Button variant="outline" size="sm" onClick={exportSession}>
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
                {currentSession.status !== 'synced' && (
                  <Button 
                    size="sm" 
                    onClick={syncToPMS}
                    disabled={syncing || !currentSession.soap_note}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="sync-pms-btn"
                  >
                    {syncing ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-1" />
                    )}
                    Sync to PMS
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recording Controls */}
        <Card className="border-slate-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={currentSpeaker} onValueChange={setCurrentSpeaker}>
                  <SelectTrigger className="w-32" data-testid="speaker-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="doctor">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-purple-600" />
                        Doctor
                      </span>
                    </SelectItem>
                    <SelectItem value="patient">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                        Patient
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>

                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger className="w-44" data-testid="language-select">
                    <SelectValue placeholder="Select Language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          {lang.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  onClick={isRecording ? stopRecording : startRecording}
                  className={isRecording ? 'bg-rose-600 hover:bg-rose-700' : 'bg-[#4A238F] hover:bg-[#3A1B72]'}
                  data-testid="record-btn"
                >
                  {isRecording ? (
                    <>
                      <Square className="h-4 w-4 mr-2" />
                      Stop Recording
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4 mr-2" />
                      Start Recording
                    </>
                  )}
                </Button>

                {audioBlob && (
                  <Button
                    onClick={transcribeAudio}
                    disabled={transcribing}
                    variant="outline"
                    data-testid="transcribe-btn"
                  >
                    {transcribing ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Transcribing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Transcribe
                      </>
                    )}
                  </Button>
                )}
              </div>

              {isRecording && (
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-rose-500 animate-pulse" />
                  <span className="text-sm text-rose-600 font-medium">Recording...</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Transcript Section */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setTranscriptExpanded(!transcriptExpanded)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Volume2 className="h-5 w-5" />
                Live Transcript
                <Badge variant="outline" className="ml-2">
                  {currentSession.transcript?.length || 0} entries
                </Badge>
              </CardTitle>
              {transcriptExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </div>
          </CardHeader>
          <AnimatePresence>
            {transcriptExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <CardContent className="pt-2">
                  <div className="max-h-64 overflow-y-auto space-y-3 mb-4">
                    {currentSession.transcript?.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">
                        Start recording to capture the conversation
                      </p>
                    ) : (
                      currentSession.transcript?.map((entry, idx) => (
                        <div
                          key={idx}
                          className={`flex ${entry.speaker === 'doctor' ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-lg ${
                              entry.speaker === 'doctor'
                                ? 'bg-purple-100 border border-blue-200'
                                : 'bg-slate-100 border border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium ${
                                entry.speaker === 'doctor' ? 'text-blue-700' : 'text-slate-600'
                              }`}>
                                {entry.speaker === 'doctor' ? 'Doctor' : 'Patient'}
                              </span>
                              <span className="text-xs text-slate-400">
                                {new Date(entry.timestamp).toLocaleTimeString()}
                              </span>
                              {entry.original_language && (
                                <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-amber-50 text-amber-700 border-amber-200">
                                  {entry.original_language} → EN
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-800">{entry.text}</p>
                            {entry.original_text && (
                              <p className="text-xs text-slate-500 mt-1.5 pt-1.5 border-t border-slate-200 italic">
                                Original: {entry.original_text}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Manual text input */}
                  <ManualTranscriptInput onAdd={addManualTranscript} />
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        {/* SOAP Note Section */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setSoapExpanded(!soapExpanded)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Clinical Summary (SOAP)
              </CardTitle>
              <div className="flex items-center gap-2">
                {!currentSession.soap_note && (
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); generateSoapNote(); }}
                    disabled={generatingSoap || !currentSession.transcript?.length}
                    className="bg-[#4A238F] hover:bg-[#3A1B72]"
                    data-testid="generate-soap-btn"
                  >
                    {generatingSoap ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-1" />
                    )}
                    Generate SOAP
                  </Button>
                )}
                {soapExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
            </div>
          </CardHeader>
          <AnimatePresence>
            {soapExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <CardContent className="pt-2">
                  {currentSession.soap_note ? (
                    <div className="space-y-4">
                      <div className="flex justify-end gap-2">
                        {editingSoap ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setEditingSoap(false)}>
                              <X className="h-4 w-4 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={saveSoapEdit} className="bg-emerald-600 hover:bg-emerald-700">
                              <Save className="h-4 w-4 mr-1" /> Save
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setEditingSoap(true); setEditedSoap(currentSession.soap_note); }}>
                              <Edit2 className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={generateSoapNote} disabled={generatingSoap}>
                              <RefreshCw className={`h-4 w-4 mr-1 ${generatingSoap ? 'animate-spin' : ''}`} /> Regenerate
                            </Button>
                          </>
                        )}
                      </div>
                      
                      {['subjective', 'objective', 'assessment', 'plan'].map(section => (
                        <div key={section} className="space-y-1">
                          <Label className="text-sm font-semibold uppercase text-slate-600">
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                          </Label>
                          {editingSoap ? (
                            <textarea
                              value={editedSoap?.[section] || ''}
                              onChange={(e) => setEditedSoap({ ...editedSoap, [section]: e.target.value })}
                              className="w-full min-h-[80px] p-3 border border-slate-200 rounded-md text-sm bg-white"
                            />
                          ) : (
                            <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                              <p className="text-sm text-slate-800 whitespace-pre-wrap">
                                {currentSession.soap_note[section]}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">
                        Record a conversation then generate the clinical summary
                      </p>
                    </div>
                  )}
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        {/* Prescription Draft Section */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setRxExpanded(!rxExpanded)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Pill className="h-5 w-5" />
                Prescription Draft
              </CardTitle>
              <div className="flex items-center gap-2">
                {!currentSession.prescription_draft && (
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); generatePrescription(); }}
                    disabled={generatingRx || !currentSession.transcript?.length}
                    className="bg-[#4A238F] hover:bg-[#3A1B72]"
                    data-testid="generate-rx-btn"
                  >
                    {generatingRx ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Pill className="h-4 w-4 mr-1" />
                    )}
                    Generate Prescription
                  </Button>
                )}
                {rxExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
            </div>
          </CardHeader>
          <AnimatePresence>
            {rxExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <CardContent className="pt-2">
                  {currentSession.prescription_draft ? (
                    <div className="space-y-4">
                      <div className="flex justify-end gap-2">
                        {editingRx ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setEditingRx(false)}>
                              <X className="h-4 w-4 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={saveRxEdit} className="bg-emerald-600 hover:bg-emerald-700">
                              <Save className="h-4 w-4 mr-1" /> Save
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setEditingRx(true); setEditedRx(currentSession.prescription_draft); }}>
                              <Edit2 className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={generatePrescription} disabled={generatingRx}>
                              <RefreshCw className={`h-4 w-4 mr-1 ${generatingRx ? 'animate-spin' : ''}`} /> Regenerate
                            </Button>
                          </>
                        )}
                      </div>

                      {/* Medications Table */}
                      <div>
                        <Label className="text-sm font-semibold text-slate-600 mb-2 block">Medications</Label>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-slate-200 rounded-lg">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium">Medication</th>
                                <th className="px-3 py-2 text-left font-medium">Dosage</th>
                                <th className="px-3 py-2 text-left font-medium">Route</th>
                                <th className="px-3 py-2 text-left font-medium">Frequency</th>
                                <th className="px-3 py-2 text-left font-medium">Duration</th>
                                {editingRx && <th className="px-3 py-2"></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {(editingRx ? editedRx : currentSession.prescription_draft)?.medications?.map((med, idx) => (
                                <tr key={idx} className="border-t border-slate-200">
                                  <td className="px-3 py-2">
                                    {editingRx ? (
                                      <Input
                                        value={med.medication_name}
                                        onChange={(e) => {
                                          const newMeds = [...editedRx.medications];
                                          newMeds[idx].medication_name = e.target.value;
                                          setEditedRx({ ...editedRx, medications: newMeds });
                                        }}
                                        className="h-8"
                                      />
                                    ) : med.medication_name}
                                  </td>
                                  <td className="px-3 py-2">
                                    {editingRx ? (
                                      <Input
                                        value={med.dosage}
                                        onChange={(e) => {
                                          const newMeds = [...editedRx.medications];
                                          newMeds[idx].dosage = e.target.value;
                                          setEditedRx({ ...editedRx, medications: newMeds });
                                        }}
                                        className="h-8"
                                      />
                                    ) : med.dosage}
                                  </td>
                                  <td className="px-3 py-2">
                                    {editingRx ? (
                                      <Input
                                        value={med.route}
                                        onChange={(e) => {
                                          const newMeds = [...editedRx.medications];
                                          newMeds[idx].route = e.target.value;
                                          setEditedRx({ ...editedRx, medications: newMeds });
                                        }}
                                        className="h-8"
                                      />
                                    ) : med.route}
                                  </td>
                                  <td className="px-3 py-2">
                                    {editingRx ? (
                                      <Input
                                        value={med.frequency}
                                        onChange={(e) => {
                                          const newMeds = [...editedRx.medications];
                                          newMeds[idx].frequency = e.target.value;
                                          setEditedRx({ ...editedRx, medications: newMeds });
                                        }}
                                        className="h-8"
                                      />
                                    ) : med.frequency}
                                  </td>
                                  <td className="px-3 py-2">
                                    {editingRx ? (
                                      <Input
                                        value={med.duration}
                                        onChange={(e) => {
                                          const newMeds = [...editedRx.medications];
                                          newMeds[idx].duration = e.target.value;
                                          setEditedRx({ ...editedRx, medications: newMeds });
                                        }}
                                        className="h-8"
                                      />
                                    ) : med.duration}
                                  </td>
                                  {editingRx && (
                                    <td className="px-3 py-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-rose-500 h-8 w-8 p-0"
                                        onClick={() => {
                                          const newMeds = editedRx.medications.filter((_, i) => i !== idx);
                                          setEditedRx({ ...editedRx, medications: newMeds });
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {editingRx && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => {
                              setEditedRx({
                                ...editedRx,
                                medications: [
                                  ...(editedRx.medications || []),
                                  { medication_name: '', dosage: '', route: 'oral', frequency: '', duration: '' }
                                ]
                              });
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" /> Add Medication
                          </Button>
                        )}
                      </div>

                      {/* ICD-10 Codes */}
                      <div>
                        <Label className="text-sm font-semibold text-slate-600 mb-2 block">ICD-10 Codes</Label>
                        <div className="flex flex-wrap gap-2">
                          {currentSession.prescription_draft.icd10_codes?.map((code, idx) => (
                            <Badge key={idx} variant="outline" className="py-1 px-2">
                              <span className="font-mono font-medium">{code.code}</span>
                              <span className="ml-2 text-slate-500">{code.description}</span>
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Notes */}
                      {currentSession.prescription_draft.notes && (
                        <div>
                          <Label className="text-sm font-semibold text-slate-600 mb-1 block">Notes</Label>
                          <p className="text-sm text-slate-600 bg-amber-50 border border-amber-200 p-3 rounded-md">
                            <AlertCircle className="h-4 w-4 inline mr-2 text-amber-600" />
                            {currentSession.prescription_draft.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Pill className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">
                        Generate prescription from the consultation transcript
                      </p>
                    </div>
                  )}
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="ai-scribe-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              AI Medical Scribe
            </h1>
            <p className="text-slate-600 mt-1">Real-time transcription & clinical documentation</p>
          </div>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            HIPAA Compliant
          </Badge>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            {renderSessionList()}
          </div>
          <div className="lg:col-span-3">
            {renderWorkspace()}
          </div>
        </div>

        {/* New Session Dialog */}
        <Dialog open={showNewSessionDialog} onOpenChange={setShowNewSessionDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Start New Scribe Session</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Patient *</Label>
                <PatientSearchDropdown
                  patients={patients}
                  selectedPatientId={selectedPatient}
                  onSelect={setSelectedPatient}
                  placeholder="Search patient by name, ID, or phone..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Link to Visit (Optional)</Label>
                <Select value={selectedVisit} onValueChange={setSelectedVisit}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select active visit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No visit link</SelectItem>
                    {visits.map(visit => (
                      <SelectItem key={visit.id} value={visit.id}>
                        {visit.visit_number} - {new Date(visit.check_in_time).toLocaleDateString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewSessionDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createSession} 
                className="bg-[#4A238F] hover:bg-[#3A1B72]"
                data-testid="create-session-btn"
              >
                Start Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}

// Manual Transcript Input Component
function ManualTranscriptInput({ onAdd }) {
  const [text, setText] = useState('');
  const [speaker, setSpeaker] = useState('doctor');

  const handleSubmit = () => {
    if (text.trim()) {
      onAdd(text, speaker);
      setText('');
    }
  };

  return (
    <div className="flex gap-2 pt-3 border-t border-slate-200">
      <Select value={speaker} onValueChange={setSpeaker}>
        <SelectTrigger className="w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="doctor">Doctor</SelectItem>
          <SelectItem value="patient">Patient</SelectItem>
        </SelectContent>
      </Select>
      <Input
        placeholder="Type transcript manually..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        className="flex-1"
      />
      <Button onClick={handleSubmit} variant="outline" size="icon">
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
